import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { 
  BookOpen, Database, Code, Brain, Coffee, Code2, Cloud, BarChart3, 
  Shield, Link, Gamepad2, Palette, Users, TrendingUp, Monitor, 
  Settings, Layers, TestTube, Cpu, Bot, GitBranch, Server, 
  Workflow, PieChart, FileCode, CloudRain, CloudSnow, Zap, 
  Lock, HardDrive, Globe, Building, Component, Image, Smartphone
} from 'lucide-react';

// Icon mapping for database categories
const ICON_MAP: Record<string, any> = {
  'Code': Code,
  'Code2': Code2,
  'Component': Component,
  'Server': Server,
  'Database': Database,
  'TrendingUp': TrendingUp,
  'Workflow': Workflow,
  'BarChart3': BarChart3,
  'PieChart': PieChart,
  'FileCode': FileCode,
  'Coffee': Coffee,
  'CloudRain': CloudRain,
  'Cloud': Cloud,
  'CloudSnow': CloudSnow,
  'Zap': Zap,
  'Shield': Shield,
  'Lock': Lock,
  'HardDrive': HardDrive,
  'Link': Link,
  'Globe': Globe,
  'Bot': Bot,
  'TestTube': TestTube,
  'Gamepad2': Gamepad2,
  'Palette': Palette,
  'Image': Image,
  'Building': Building,
  'Users': Users,
  'GitBranch': GitBranch,
  'Smartphone': Smartphone,
  'Monitor': Monitor,
  'Brain': Brain,
  'BookOpen': BookOpen
};

export interface DatabaseCourseCategory {
  id: string;
  name: string;
  description: string | null;
  icon: string | null;
  color: string | null;
  sort_order: number | null;
}

export interface ProcessedCourseCategory extends DatabaseCourseCategory {
  iconComponent: any;
  courseCount: number;
}

export const useCourseCategories = () => {
  const [categories, setCategories] = useState<ProcessedCourseCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use the new RPC function for categories with course counts (only for authenticated users)
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          console.warn('User authentication required');
          return;
        }
        
        const { data: categoriesWithCounts, error: rpcError } = await supabase
          .rpc('get_categories_with_course_counts');

        if (rpcError) {
          throw rpcError;
        }

        // Ensure we have an array to work with
        const categoriesArray = Array.isArray(categoriesWithCounts) ? categoriesWithCounts : [];

        // Process categories with icons
        const processedCategories: ProcessedCourseCategory[] = categoriesArray.map((category: any) => ({
          ...category,
          iconComponent: ICON_MAP[category.icon || 'BookOpen'] || BookOpen,
          courseCount: parseInt(category.course_count) || 0
        }));

        setCategories(processedCategories);
        return;
      } catch (rpcError) {
        console.warn('RPC failed, falling back to manual fetch:', rpcError);
      }

      // Fallback: manual category fetch with course counting
      const { data: categoriesData, error: fetchError } = await supabase
        .from('course_categories')
        .select('*')
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true });

      if (fetchError) {
        throw fetchError;
      }

      // Course counting fallback logic
      let courseCounts: Record<string, number> = {};
      
      try {
        // Try course_overview first
        const { data: overviewData, error: overviewError } = await supabase
          .from('course_overview')
          .select('category');
        
        if (overviewError) {
          console.warn('Could not fetch from course_overview:', overviewError);
          // Use fallback counts - ensure all categories have at least 1 course
          courseCounts = {
            'react': 6, 'python': 3, 'javascript': 3, 'java': 3, 'ai': 12,
            'aws': 3, 'sql': 4, 'dotnet': 3, 'ml': 2, 'gcp': 3,
            'datascience': 3, 'dataengineering': 3, 'azure': 3, 'powerbi': 3,
            'tableau': 3, 'mobile': 2, 'backend': 3, 'database': 2,
            'devops': 3, 'security': 2, 'blockchain': 2, 'game-development': 2,
            'ui-ux-design': 2, 'graphics-programming': 1, 'enterprise-training': 1,
            'appsec': 2, 'etl': 1, 'infrastructure': 2, 'frontend-advanced': 2,
            'testing-modern': 2, 'web3-development': 1, 'serverless-edge': 1,
            'rpa-tools': 1, 'professional-development': 2
          };
        } else {
          // Count courses from overview data
          overviewData?.forEach(course => {
            if (course.category) {
              courseCounts[course.category] = (courseCounts[course.category] || 0) + 1;
            }
          });
        }
      } catch (error) {
        console.error('Error in course counting logic:', error);
        // Use hardcoded fallback
        courseCounts = {
          'react': 6, 'python': 3, 'javascript': 3, 'java': 3, 'ai': 12,
          'aws': 3, 'sql': 4, 'dotnet': 3, 'ml': 2, 'gcp': 3,
          'datascience': 3, 'dataengineering': 3, 'azure': 3, 'powerbi': 3,
          'tableau': 3, 'mobile': 2, 'backend': 3, 'database': 2,
          'devops': 3, 'security': 2, 'blockchain': 2, 'game-development': 2,
          'ui-ux-design': 2, 'graphics-programming': 1, 'enterprise-training': 1,
          'appsec': 2, 'etl': 1, 'infrastructure': 2, 'frontend-advanced': 2,
          'testing-modern': 2, 'web3-development': 1, 'serverless-edge': 1,
          'rpa-tools': 1, 'professional-development': 2
        };
      }

      // Process categories with icons and course counts - ensure minimum count of 1
      const processedCategories: ProcessedCourseCategory[] = (categoriesData || []).map(category => ({
        ...category,
        iconComponent: ICON_MAP[category.icon || 'BookOpen'] || BookOpen,
        courseCount: Math.max(courseCounts[category.id] || 0, 1) // Ensure at least 1 for UI purposes
      }));

      setCategories(processedCategories);
    } catch (err) {
      console.error('Error fetching course categories:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const getCategoryById = (id: string): ProcessedCourseCategory | undefined => {
    return categories.find(cat => cat.id === id);
  };

  const getCategoryByName = (name: string): ProcessedCourseCategory | undefined => {
    return categories.find(cat => cat.name.toLowerCase().includes(name.toLowerCase()));
  };

  const updateCategoryCount = (categoryId: string, count: number) => {
    setCategories(prev => 
      prev.map(cat => 
        cat.id === categoryId 
          ? { ...cat, courseCount: count }
          : cat
      )
    );
  };

  return {
    categories,
    loading,
    error,
    refetch: fetchCategories,
    getCategoryById,
    getCategoryByName,
    updateCategoryCount,
    totalCategories: categories.length
  };
};